import { UserRole } from '@comp-sys/shared';

/**
 * 权限判断遵循「隔离语义」:所有函数接收的 `role` 应为当前激活角色(activeRole),
 * 与后端 RolesGuard 一致。多角色用户仅拥有其 activeRole 的权限。
 */

/**
 * Admin roles that can manage competitions (create, edit, delete, status change)
 */
export const ADMIN_ROLES: UserRole[] = [
  UserRole.SYSTEM_ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
];

/**
 * Roles that can enroll in competitions
 */
export const ENROLLMENT_ROLES: UserRole[] = [
  UserRole.CONTESTANT,
];

/**
 * Check if a role is an admin role
 */
export function isAdminRole(role: UserRole | undefined): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

/**
 * Check if the current user is the owner (creator) of a competition
 */
export function isCompetitionOwner(
  createdById: string | undefined | null,
  currentUserId: string | undefined,
): boolean {
  return !!createdById && !!currentUserId && createdById === currentUserId;
}

/**
 * Check if a role can administer a competition (full CRUD + status)
 */
export function canAdminCompetition(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  if (role === UserRole.COMPETITION_MANAGER) {
    return isCompetitionOwner(createdById, currentUserId);
  }
  return false;
}

/**
 * Check if a role can enroll in competitions
 */
export function canEnroll(role: UserRole | undefined): boolean {
  return !!role && ENROLLMENT_ROLES.includes(role);
}

/**
 * Check if a role can calculate rankings
 */
export function canCalculateRankings(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  if (role === UserRole.COMPETITION_MANAGER) {
    return isCompetitionOwner(createdById, currentUserId);
  }
  return false;
}

/**
 * Check if a role can manage rankings
 */
export function canManageRankings(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  if (role === UserRole.COMPETITION_MANAGER) {
    return isCompetitionOwner(createdById, currentUserId);
  }
  return false;
}

/**
 * Check if a role can manage awards (create, edit, delete) — CM excluded
 */
export function canManageAwards(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  return false;
}

/**
 * Check if a role can auto-assign awards — CM allowed for own competitions
 */
export function canAutoAssignAwards(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  if (role === UserRole.COMPETITION_MANAGER) {
    return isCompetitionOwner(createdById, currentUserId);
  }
  return false;
}

/**
 * Check if a role can view a competition (all authenticated users)
 */
export function canViewCompetition(role: UserRole | undefined): boolean {
  return true;
}

/**
 * Check if a role can manage teams (add/remove members, edit, delete)
 */
export function canManageTeams(
  role: UserRole | undefined,
  createdById?: string | null,
  currentUserId?: string,
): boolean {
  if (isAdminRole(role)) return true;
  return false;
}

/**
 * Roles that have personal profiles (need profile-setup after registration)
 */
export const PROFILE_CREATABLE_ROLES: UserRole[] = [
  UserRole.CONTESTANT,
  UserRole.JUDGE,
  UserRole.COACH,
  UserRole.VOLUNTEER,
];

/**
 * Roles without personal profiles (need name-setup after registration)
 */
export const NON_PROFILE_ROLES: UserRole[] = [
  UserRole.SYSTEM_ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.COMPETITION_MANAGER,
  UserRole.SCHOOL,
  UserRole.INSTITUTION,
];

/**
 * Roles that can create profiles for themselves (same as PROFILE_CREATABLE_ROLES)
 */
export const PROFILE_SELF_CREATABLE_ROLES = PROFILE_CREATABLE_ROLES;

/**
 * Roles that can create profiles for others
 */
export const PROFILE_ADMIN_CREATABLE_ROLES: UserRole[] = [
  UserRole.SYSTEM_ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.SCHOOL,
];

/**
 * Roles available for self-registration in system settings
 */
export const SELF_REGISTERABLE_ROLES: UserRole[] = [
  UserRole.CONTESTANT,
  UserRole.JUDGE,
  UserRole.COACH,
  UserRole.VOLUNTEER,
  UserRole.SCHOOL,
  UserRole.INSTITUTION,
];

export function isProfileCreatableRole(role: UserRole | undefined): boolean {
  return !!role && PROFILE_CREATABLE_ROLES.includes(role);
}

export function isNonProfileRole(role: UserRole | undefined): boolean {
  return !!role && NON_PROFILE_ROLES.includes(role);
}

export function canCreateProfileForSelf(role: UserRole | undefined): boolean {
  return !!role && PROFILE_SELF_CREATABLE_ROLES.includes(role);
}

export function canCreateProfileForOthers(role: UserRole | undefined): boolean {
  return !!role && PROFILE_ADMIN_CREATABLE_ROLES.includes(role);
}

/**
 * Check if a role can create users (SYSTEM_ADMIN and SUPER_ADMIN only)
 */
export function canCreateUser(role: UserRole | undefined): boolean {
  return role === UserRole.SYSTEM_ADMIN || role === UserRole.SUPER_ADMIN;
}

/**
 * 默认「可编辑他人档案」的角色（= 现状行为，零回归）。
 * 与后端 getDefaultProfileConfig().profileEditRoles 保持一致。
 */
export const DEFAULT_PROFILE_EDIT_ROLES: UserRole[] = [
  UserRole.SYSTEM_ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.SCHOOL,
];

/**
 * 粗粒度前端判断（列表/行内入口用）。
 * CM 的赛事归属前端无法 cheaply 判断 → 在矩阵内即放行显示，保存时后端 canAccessAsync 兜底。
 * 详情页请优先用后端返回的 profile.canEdit（精确）。
 */
export function canEditProfile(
  role: UserRole | undefined,
  profile: { userId: string; schoolId?: string | null } | undefined,
  currentUser: { id: string; schoolId?: string | null },
  editRoles?: UserRole[],
): boolean {
  if (!role) return false;
  if (profile && profile.userId === currentUser.id) return true;
  const roles = editRoles?.length ? editRoles : DEFAULT_PROFILE_EDIT_ROLES;
  if (!roles.includes(role)) return false;
  if (isAdminRole(role)) return true;
  if (role === UserRole.SCHOOL) return !!profile && profile.schoolId === currentUser.schoolId;
  if (role === UserRole.COMPETITION_MANAGER) return true; // 粗判放行，后端兜底归属
  return false;
}
